import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null=checking, false=unauthenticated, object=user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) {
      localStorage.setItem("access_token", data.access_token);
    }
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("access_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, fetchMe, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function can(user, action) {
  if (!user || !user.role) return false;
  const role = user.role;
  const rules = {
    manageProperties: ["admin", "editor"],
    deleteProperty: ["admin"],
    manageTransactions: ["admin", "editor"],
    manageAlerts: ["admin", "editor"],
    manageUsers: ["admin"],
    viewAudit: ["admin", "auditor"],
    sendTestEmail: ["admin"],
    exportReports: ["admin", "editor", "viewer", "auditor"],
  };
  return (rules[action] || []).includes(role);
}
